import { InvalidProjectPathError, normalizeProjectPath, type ProjectRefusalCode } from './paths';
import {
  addFile, addFolder, deleteFile, deleteFolder, filesUnder, folderPaths, isDefaultRootFile, isLoadBearing, renameFile,
  renameFolder, writeFile, type ProjectTree,
} from './tree';

export type { ProjectRefusalCode } from './paths';

/**
 * One change to a project, as the file tree, the editor and (later) an agent's tools make it: one verb and its inputs.
 * Paths are project-relative (`src/client/systems/Snake.ts`); a folder is named by its path (`src/client/systems`).
 */
export type ProjectOperation =
  | { kind: 'createFile'; path: string; content: string }
  | { kind: 'writeFile'; path: string; content: string }
  | { kind: 'renameFile'; from: string; to: string }
  | { kind: 'deleteFile'; path: string }
  | { kind: 'createFolder'; path: string }
  | { kind: 'renameFolder'; from: string; to: string }
  | { kind: 'deleteFolder'; path: string };

/** An operation that was taken: the project after it, and every path it changed. */
export interface ProjectChange {
  ok: true;
  tree: ProjectTree;
  /**
   * Every path whose content or existence the operation changed: the files it created, rewrote (the system registries
   * included), moved from and to, or removed — in the old tree's order, then the new files in the new tree's order — and
   * then each folder that went away or appeared. Empty when the project is as it was (a write of what a file holds).
   */
  changed: string[];
}

/** An operation that was refused: why, as a code to act on and a reason a person reads. Nothing changed. */
export interface ProjectRefusal {
  ok: false;
  code: ProjectRefusalCode;
  reason: string;
}

export type ProjectOperationResult = ProjectChange | ProjectRefusal;

/**
 * A name a person gives a file or folder: letters, digits, `_`, `-` and dots, starting with a letter, a digit or `_`,
 * and not ending in a dot. The editor has always made names so; a new file's and a renamed one's are held to it.
 */
export const isValidFileName = (name: string): boolean => /^[A-Za-z0-9_][A-Za-z0-9_.-]*$/.test(name) && !name.endsWith('.');

/** Refuses `raw` when a name it introduces is not valid: its last segment, and each folder on the way the project lacks. */
function refuseInvalidNames(tree: ProjectTree, raw: string): void {
  const segments = normalizeProjectPath(raw).split('/');
  const folders = new Set(folderPaths(tree));
  segments.forEach((name, i) => {
    const isExistingFolder = i < segments.length - 1 && folders.has(segments.slice(0, i + 1).join('/'));
    if (!isExistingFolder && !isValidFileName(name)) {
      throw new InvalidProjectPathError(raw, `"${name}" is not a valid name: use letters, digits, _, - and ., beginning with a letter, digit or _ and not ending in .`, 'invalid_name');
    }
  });
}

/** Refuses deleting `path` when the sandbox always keeps it: a side's root `api`, `types`, `utils` or `constants` file. */
function refuseKeptFile(raw: string, path: string): void {
  if (isDefaultRootFile(path)) throw new InvalidProjectPathError(raw, 'the sandbox keeps each side’s root api, types, utils and constants files', 'protected');
}

/** The project after `op`, by the tree's mechanics (which refuse what the project cannot hold) and the rules a person's changes follow. */
function perform(tree: ProjectTree, op: ProjectOperation): ProjectTree {
  switch (op.kind) {
    case 'createFile': {
      const next = addFile(tree, op.path, op.content);
      refuseInvalidNames(tree, op.path);
      return next;
    }
    case 'writeFile':
      return writeFile(tree, op.path, op.content);
    case 'renameFile': {
      const next = renameFile(tree, op.from, op.to);
      refuseInvalidNames(tree, op.to);
      return next;
    }
    case 'deleteFile': {
      const next = deleteFile(tree, op.path);
      refuseKeptFile(op.path, normalizeProjectPath(op.path));
      return next;
    }
    case 'createFolder': {
      const next = addFolder(tree, op.path);
      refuseInvalidNames(tree, op.path);
      return next;
    }
    case 'renameFolder': {
      const next = renameFolder(tree, op.from, op.to);
      refuseInvalidNames(tree, op.to);
      return next;
    }
    case 'deleteFolder': {
      const next = deleteFolder(tree, op.path);
      const kept = filesUnder(tree, op.path).find(isDefaultRootFile);
      if (kept !== undefined) throw new InvalidProjectPathError(op.path, `it holds ${kept}, which the sandbox always keeps`, 'protected');
      return next;
    }
  }
}

/** Every path `after` differs from `before` in — see `ProjectChange.changed`. */
export function projectChanges(before: ProjectTree, after: ProjectTree): string[] {
  const changed: string[] = [];
  for (const [path, code] of Object.entries(before.files)) if (!Object.hasOwn(after.files, path) || after.files[path] !== code) changed.push(path);
  for (const path of Object.keys(after.files)) if (!Object.hasOwn(before.files, path)) changed.push(path);
  const [beforeFolders, afterFolders] = [folderPaths(before), folderPaths(after)];
  const [hadFolder, hasFolder] = [new Set(beforeFolders), new Set(afterFolders)];
  for (const folder of beforeFolders) if (!hasFolder.has(folder)) changed.push(folder);
  for (const folder of afterFolders) if (!hadFolder.has(folder)) changed.push(folder);
  return changed;
}

/**
 * Applies one operation to a project. Pure: `tree` is never changed, and a refusal is returned, never thrown — with a
 * code (`ProjectRefusalCode`) and a reason a person reads. What it refuses, by operation:
 * - `createFile`: a path the project cannot hold, one taken (any letter case, or a file where a folder must be), an
 *   invalid name (the file's, or a folder's it makes);
 * - `writeFile`: a file the project does not have;
 * - `renameFile`: a missing file, one the project cannot run without, a taken or invalid `to`;
 * - `deleteFile`: a missing file, one the project cannot run without, a side's root api/types/utils/constants file;
 * - `createFolder`: a folder or file already there (any letter case), an invalid name;
 * - `renameFolder`: a missing folder, one holding a file the project cannot run without, a `to` inside it, taken or
 *   invalid; every file under it moves;
 * - `deleteFolder`: a missing folder, one holding a file the project cannot run without or always keeps; every file
 *   under it goes.
 * Errors other than a refusal (a bug) are thrown.
 */
export function applyProjectOperation(tree: ProjectTree, op: ProjectOperation): ProjectOperationResult {
  try {
    const next = perform(tree, op);
    return { ok: true, tree: next, changed: projectChanges(tree, next) };
  } catch (err) {
    if (err instanceof InvalidProjectPathError) return { ok: false, code: err.code, reason: err.reason };
    throw err;
  }
}

/** Whether the project has `path` as a file, exactly — `false` for a path it could not hold. */
const hasFile = (tree: ProjectTree, path: string): boolean => {
  try {
    return Object.hasOwn(tree.files, normalizeProjectPath(path));
  } catch (err) {
    if (err instanceof InvalidProjectPathError) return false;
    throw err;
  }
};

/** Whether the project has `folder`, exactly — `false` for a path it could not hold. */
const hasFolder = (tree: ProjectTree, folder: string): boolean => {
  try {
    return folderPaths(tree).includes(normalizeProjectPath(folder));
  } catch (err) {
    if (err instanceof InvalidProjectPathError) return false;
    throw err;
  }
};

/** Whether `deleteFile` would take `path` — what the file tree offers a delete for. */
export const canDeleteFile = (tree: ProjectTree, path: string): boolean =>
  hasFile(tree, path) && !isLoadBearing(tree, path) && !isDefaultRootFile(path);

/** Whether `renameFile` would take `path` to a free, valid name — what the file tree offers a rename for. */
export const canRenameFile = (tree: ProjectTree, path: string): boolean => hasFile(tree, path) && !isLoadBearing(tree, path);

/** Whether `deleteFolder` would take `folder` — what the file tree offers a folder delete for. */
export const canDeleteFolder = (tree: ProjectTree, folder: string): boolean =>
  hasFolder(tree, folder) && filesUnder(tree, folder).every((path) => !isLoadBearing(tree, path) && !isDefaultRootFile(path));

/** Whether `renameFolder` would take `folder` to a free, valid name — what the file tree offers a folder rename for. */
export const canRenameFolder = (tree: ProjectTree, folder: string): boolean =>
  hasFolder(tree, folder) && filesUnder(tree, folder).every((path) => !isLoadBearing(tree, path));
